import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import SearchableSelectInput from "../input/searchable-select-input";
import NumberInput from "../input/number-input";
import TimeInput from "../input/time-input";
import DateInput from "../input/date-input";
import FixedSelectInput from "../input/fixed-select-input";
import TextInput from "../input/text-input";
import NumberUnitInput from "../input/number-unit-input";
import TimeGeneralInput from "../input/time-general-input";
import DateGeneralInput from "../input/date-general-input";
import TimestampInput from "../input/timestamp-input";

const getSelector = (spec) => {
  if (spec.hasEnums) {
    return "FIXED_SELECT";
  } else if (spec.type === "String") {
    return "TEXT";
  } else if (spec.type === "Number") {
    return "NUMBER";
  } else if (
    spec.type === "Widget" ||
    spec.type === "Length" ||
    spec.type === "Duration" ||
    spec.type === "Temperature" ||
    spec.type === "TemperatureDiff"
  ) {
    return "NUMBER_UNIT";
  } else if (spec.type === "Time") {
    return "TIME";
  } else if (spec.type === "TimeGeneral") {
    return "TIME_GENERAL";
  } else if (spec.type === "Date") {
    return "DATE";
  } else if (spec.type === "DateGeneral") {
    return "DATE_GENERAL";
  } else if (spec.type === "Timestamp") {
    return "TIMESTAMP";
  } else if (
    spec.type === "SpecialTime" ||
    spec.type === "SpecialDate" ||
    spec.type === "RelativeDate"
  ) {
    return "FIXED_SELECT";
  } else {
    return "DYNAMIC_SELECT";
  }
};

const DataSelector = ({
  varName,
  spec,
  widgetized,
  widgetizable,
  widgetizeVar,
  setLocalWidgetizeVar,
  onSave,
  onCancel,
  onFetch,
}) => {
  // zIndex for automation input (from theme.js)
  const automationInputZIndex = 1450;

  const selectMap = {
    FIXED_SELECT: (f, idx) => (
      <Box
        sx={{
          height: "100%",
        }}
      >
        <FixedSelectInput
          valueSpec={f}
          onChange={(value) => handleValueChange(f, idx, value)}
          onError={() => handleErrorChange(idx)}
          menuDirection="top"
          zIndex={automationInputZIndex}
        ></FixedSelectInput>
      </Box>
    ),
    DYNAMIC_SELECT: (f, idx) => (
      <SearchableSelectInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
        onError={() => handleErrorChange(idx)}
        fetchValueSpec={dynamicValueSpecFetch}
        listPosition="top"
        zIndex={automationInputZIndex}
      ></SearchableSelectInput>
    ),
    NUMBER: (f, idx) => (
      <NumberInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
        onError={() => handleErrorChange(idx)}
      ></NumberInput>
    ),
    TIME: (f, idx) => (
      <TimeInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
        embedded={true}
      ></TimeInput>
    ),
    TIME_GENERAL: (f, idx) => (
      <TimeGeneralInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
        zIndex={automationInputZIndex}
      ></TimeGeneralInput>
    ),
    DATE_GENERAL: (f, idx) => (
      <DateGeneralInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
        zIndex={automationInputZIndex}
      ></DateGeneralInput>
    ),
    DATE: (f, idx) => (
      <DateInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
      ></DateInput>
    ),
    TIMESTAMP: (f, idx) => (
      <TimestampInput
        valueSpec={f}
        onChange={(value) => handleValueChange(f, idx, value)}
      ></TimestampInput>
    ),
    TEXT: (f, idx) => (
      <Box
        sx={{
          height: "100%",
        }}
      >
        <TextInput
          valueSpec={f}
          onChange={(value) => handleValueChange(f, idx, value)}
        ></TextInput>
      </Box>
    ),
    NUMBER_UNIT: (f, idx) => (
      <NumberUnitInput
        valueSpec={f}
        onChange={(value, unit) =>
          handleValueChange(f, idx, value.toString() + unit)
        }
        onError={() => handleErrorChange(idx)}
      ></NumberUnitInput>
    ),
  };

  const [selector, setSelector] = useState(null);
  const [entityType, setEntityType] = useState(false);
  const [selectable, setSelectable] = useState(true);
  const [valueSpecs, setValueSpecs] = useState([]);
  const [values, setValues] = useState([]);
  const errorsRef = useRef([]);
  const [isValid, setIsValid] = useState(true);

  const [w, setW] = useState(widgetized);

  const checkValidity = (errorArray, valueArray) => {
    const hasErrors = errorArray.indexOf(true) !== -1;
    const hasValues = valueArray.length > 0;
    const valid = hasValues && !hasErrors;
    setIsValid(valid);
    console.log("errorArray", errorArray);
    console.log("isValid", valid);
    return valid;
  };

  useEffect(() => {
    setSelector(getSelector(spec));
    const isEntityType = spec.entity || false;
    setEntityType(isEntityType);
    const vs = [];
    const vals = [];
    const errs = [];
    for (const item of spec.items) {
      const value = item.value;
      const valueSpec = item.valueSpec;
      const v = populateValueSpec(value, valueSpec, isEntityType);
      vals.push(item.value);
      vs.push(v);
      errs.push(false);
    }
    setValues(vals);
    errorsRef.current = errs;
    setValueSpecs(vs);
    // TODO: deprecate?
    setSelectable(spec.type !== "basic.widget.Widget" || !widgetized);
    checkValidity(errs, vals);
  }, []);

  const handleValueChange = (valueSpec, idx, newValue) => {
    const newValues = [...values];
    newValues[idx] = newValue;
    setValues(newValues);

    const newValueSpecs = [...valueSpecs];
    newValueSpecs[idx] = { ...valueSpec, value: newValue };
    setValueSpecs(newValueSpecs);

    errorsRef.current[idx] = false;
    checkValidity(errorsRef.current, newValues);
  };

  const handleErrorChange = (idx) => {
    console.log("handleErrorChange", idx);
    errorsRef.current[idx] = true;
    checkValidity(errorsRef.current, values);
  };

  const dynamicValueSpecFetch = async (inputValue) => {
    const varData = {
      var: varName,
      input: inputValue,
    };
    return await onFetch(varData);
  };

  const handleAdd = async () => {
    const vs = await dynamicValueSpecFetch("");
    const value = vs.value;
    const v = populateValueSpec(value, vs.valueSpec, entityType);
    const newValues = [...values, value];
    setValues(newValues);
    errorsRef.current = [...errorsRef.current, true];
    setValueSpecs([...valueSpecs, v]);
    checkValidity(errorsRef.current, newValues);
  };

  const populateValueSpec = (value, valueSpec, isEntityType) => {
    const v = { ...valueSpec, value, titleBreak: isEntityType };
    return v;
  };

  const handleDelete = (idx) => {
    const tValues = [...values];
    tValues.splice(idx, 1);
    const tValueSpecs = [...valueSpecs];
    tValueSpecs.splice(idx, 1);
    errorsRef.current.splice(idx, 1);
    setValues(tValues);
    setValueSpecs(tValueSpecs);
    checkValidity(errorsRef.current, tValues);
  };

  const handleSave = async () => {
    if (spec.type === "basic.widget.Widget") {
      // TODO: deprecate?
      if (!values[0]) {
        return;
      }
      widgetizeVar(varName, w);
      setLocalWidgetizeVar(varName, w);
    } else {
      widgetizeVar(varName, w);
    }
    if (!selectable) {
      onCancel();
      return;
    }

    if (!isValid) {
      return;
    }
    const varData = {
      var: varName,
      inputs: [...values],
    };
    await onSave(varData);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {widgetizable && (
          <>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={w}
                    onChange={(e) => {
                      setW(e.target.checked);
                      if (spec.type === "basic.widget.Widget") {
                        setSelectable(!e.target.checked);
                        if (!e.target.checked) {
                          handleValueChange(valueSpecs[0], 0, "");
                        }
                      }
                    }}
                  />
                }
                label={
                  spec.type === "basic.widget.Widget"
                    ? "Auto create a widget in card"
                    : "Override value in dashboard card"
                }
              />
            </Box>
          </>
        )}
        {selectMap[selector] !== undefined && selectable && (
          <>
            {valueSpecs.map((valueSpec, idx) => (
              <Box key={`param-${varName}-${idx}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {spec.repeated && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDelete(idx)}
                      disabled={valueSpecs.length <= 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  <Box sx={{ flex: 1 }}>
                    {selectMap[selector](valueSpec, idx)}
                  </Box>
                </Box>
              </Box>
            ))}
          </>
        )}
        {spec.repeated && (
          <Box>
            <Button
              size="medium"
              fullWidth
              onClick={handleAdd}
              sx={{
                minWidth: { xs: "36px", sm: "auto" },
              }}
            >
              <AddIcon />
            </Button>
          </Box>
        )}
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Button
              size="medium"
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              sx={{
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="medium"
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={!isValid}
              sx={{
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DataSelector;
